import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import Card from "@mui/material/Card";
import Footer from "examples/Footer";
import { CardContent, Grid, CircularProgress, Avatar } from "@mui/material";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import { getCompany, updateCompany } from "services/companyService";
import { toast } from "react-toastify";

function CompanyManagement() {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [companyData, setCompanyData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    logo: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  // Fetch company details on component mount
  useEffect(() => {
    fetchCompanyDetails();
  }, []);

  const fetchCompanyDetails = async () => {
    setLoading(true);
    try {
      const response = await getCompany();
      if (response.success) {
        setCompanyData(response.data);
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch company details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Please select an image smaller than 5MB");
        return;
      }

      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number (must be exactly 10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(companyData.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", companyData.name);
      formData.append("address", companyData.address);
      formData.append("phone", companyData.phone);
      formData.append("email", companyData.email);

      if (logoFile) {
        formData.append("logo", logoFile);
      }

      const response = await updateCompany(formData);

      if (response.success) {
        toast.success("Company details updated successfully");
        fetchCompanyDetails();
        setLogoFile(null);
      }
    } catch (error) {
      toast.error(error.message || "Failed to update company details");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox
          py={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress color="info" size={60} />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={1.5}>
        <ArgonBox mb={1}>
          <Card
            sx={{
              boxShadow: 6,
              borderRadius: 4,
              overflow: "hidden",
              background: "linear-gradient(180deg, #ffffff 0%, #f6f9fc 100%)",
            }}
          >
            {/* Header */}
            <ArgonBox px={2} py={1} borderBottom="1px solid #f0f0f0" sx={{ background: "#fafafa" }}>
              <ArgonTypography variant="h5" fontWeight="bold" color="primary">
                🏢 Company Management
              </ArgonTypography>
            </ArgonBox>

            {/* Body */}
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* Logo Section */}
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                  >
                    <Avatar
                      src={logoPreview}
                      alt="Company Logo"
                      sx={{
                        width: 150,
                        height: 150,
                        mb: 1,
                        border: "3px solid #e0e0e0",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    />
                    <ArgonButton
                      component="label"
                      variant="outlined"
                      color="info"
                      size="small"
                      sx={{ borderRadius: "8px", fontSize: "0.75rem", py: 0.5, px: 2 }}
                    >
                      Upload Logo
                      <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                    </ArgonButton>
                  </Grid>

                  {/* Company Name */}
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                      Company Name *
                    </ArgonTypography>
                    <ArgonInput
                      type="text"
                      name="name"
                      placeholder="Enter company name"
                      value={companyData.name}
                      onChange={handleInputChange}
                      required
                      fullWidth
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                      Email Address *
                    </ArgonTypography>
                    <ArgonInput
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      value={companyData.email}
                      onChange={handleInputChange}
                      required
                      fullWidth
                    />
                  </Grid>

                  {/* Phone */}
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                      Phone Number *
                    </ArgonTypography>
                    <ArgonInput
                      type="tel"
                      name="phone"
                      placeholder="Enter phone number"
                      value={companyData.phone}
                      onChange={handleInputChange}
                      required
                      fullWidth
                    />
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                      Address *
                    </ArgonTypography>
                    <ArgonInput
                      type="text"
                      name="address"
                      placeholder="Enter complete address"
                      value={companyData.address}
                      onChange={handleInputChange}
                      required
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>

                {/* Footer Buttons */}
                <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => fetchCompanyDetails()}
                    disabled={submitting}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      px: 2,
                    }}
                  >
                    Reset
                  </ArgonButton>
                  <ArgonButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={submitting}
                    size="small"
                    sx={{
                      borderRadius: "8px",
                      px: 3,
                      fontWeight: "bold",
                    }}
                  >
                    {submitting ? <CircularProgress size={16} color="white" /> : "Save Changes"}
                  </ArgonButton>
                </ArgonBox>
              </form>
            </CardContent>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CompanyManagement;
